import { useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
  Settings as SettingsIcon,
  AttachMoney as MoneyIcon,
  Shield as ShieldIcon,
  Videocam as VideoIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { useEngagementEditor } from './EngagementEditorContext';
import { AdviseDataRoomFile } from '../../../../types/advise';

interface FileCategory {
  key: string;
  label: string;
  icon: React.ReactNode;
  colorClass: string;
}

const FILE_CATEGORIES: FileCategory[] = [
  {
    key: 'supplier_proposals',
    label: 'Supplier Proposals',
    icon: <FileIcon />,
    colorClass: 'text-blue-600'
  },
  {
    key: 'technical_docs',
    label: 'Technical Documentation',
    icon: <SettingsIcon />,
    colorClass: 'text-purple-600'
  },
  {
    key: 'pricing_contracts',
    label: 'Pricing & Contracts',
    icon: <MoneyIcon />,
    colorClass: 'text-emerald-600'
  },
  {
    key: 'security_compliance',
    label: 'Security & Compliance',
    icon: <ShieldIcon />,
    colorClass: 'text-amber-600'
  },
  {
    key: 'demo_recordings',
    label: 'Demo Recordings',
    icon: <VideoIcon />,
    colorClass: 'text-rose-600'
  },
  {
    key: 'other',
    label: 'Other Documents',
    icon: <FolderIcon />,
    colorClass: 'text-slate-600'
  }
];

export const DataRoomPanel = () => {
  const { engagement, canEdit, editEngagement } = useEngagementEditor();
  // TODO: uploadingCategory is set and cleared in the same sync tick (no async boundary), so the spinner never shows.
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);
  const [notes, setNotes] = useState(engagement.data_room_notes || '');
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const files = useMemo(() => engagement.data_room_files || [], [engagement.data_room_files]);

  const filesByCategory = useMemo(() => {
    const grouped: Record<string, AdviseDataRoomFile[]> = {};
    FILE_CATEGORIES.forEach((cat) => {
      grouped[cat.key] = files.filter((f) => f.section === cat.key);
    });
    return grouped;
  }, [files]);

  const handleUpload = useCallback(
    (category: string, fileList: FileList | null) => {
      if (!fileList?.length) return;
      setUploadingCategory(category);

      const newFiles: AdviseDataRoomFile[] = Array.from(fileList).map((file) => ({
        file_url: URL.createObjectURL(file),
        file_name: file.name,
        section: category,
        uploaded_date: new Date().toISOString()
      }));

      editEngagement({ data_room_files: [...files, ...newFiles] });
      setUploadingCategory(null);
    },
    [files, editEngagement]
  );

  const handleDeleteFile = useCallback(
    (fileToDelete: AdviseDataRoomFile) => {
      if (fileToDelete.file_url?.startsWith('blob:')) {
        URL.revokeObjectURL(fileToDelete.file_url);
      }
      const updated = files.filter((f) => f !== fileToDelete);
      editEngagement({ data_room_files: updated });
    },
    [files, editEngagement]
  );

  const handleNotesBlur = useCallback(() => {
    if (notes !== engagement.data_room_notes) {
      editEngagement({ data_room_notes: notes });
    }
  }, [notes, engagement.data_room_notes, editEngagement]);

  const totalFiles = files.length;

  return (
    <Box className="flex flex-col gap-4">
      <Box className="flex items-center justify-between">
        <Typography
          variant="body2"
          color="textSecondary">
          Centralized document storage for the evaluation.
        </Typography>
        <Chip
          label={`${totalFiles} file${totalFiles !== 1 ? 's' : ''}`}
          size="small"
          color={totalFiles > 0 ? 'primary' : 'default'}
        />
      </Box>

      {/* File Categories Grid */}
      <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FILE_CATEGORIES.map((cat) => {
          const catFiles = filesByCategory[cat.key] || [];
          const isUploading = uploadingCategory === cat.key;

          return (
            <Card
              key={cat.key}
              variant="outlined">
              <CardContent>
                <Box className="flex items-center gap-2 mb-3">
                  <span className={cat.colorClass}>{cat.icon}</span>
                  <Typography variant="subtitle2">{cat.label}</Typography>
                  {catFiles.length > 0 && (
                    <Chip
                      label={catFiles.length}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>

                {catFiles.length > 0 && (
                  <Box className="flex flex-col gap-1 mb-3">
                    {/* TODO: key on file identity, not array index — deleting mid-list reconciles onto the wrong row. */}
                    {catFiles.map((file, idx) => (
                      <Box
                        key={idx}
                        className={'flex items-center gap-2 p-1.5 ' + 'rounded bg-gray-50 group'}>
                        <FileIcon
                          fontSize="small"
                          color="action"
                        />
                        <Typography
                          variant="caption"
                          className="flex-1 truncate">
                          {file.file_name}
                        </Typography>
                        {file.file_url && (
                          <IconButton
                            size="small"
                            href={file.file_url}
                            target="_blank"
                            rel="noopener noreferrer">
                            <OpenIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                        {canEdit && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteFile(file)}
                            className={'opacity-0 ' + 'group-hover:opacity-100'}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {canEdit && (
                  <>
                    <input
                      type="file"
                      multiple
                      hidden
                      ref={(el) => {
                        fileInputRefs.current[cat.key] = el;
                      }}
                      onChange={(e) => handleUpload(cat.key, e.target.files)}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={isUploading ? <CircularProgress size={14} /> : <UploadIcon />}
                      disabled={isUploading}
                      onClick={() => fileInputRefs.current[cat.key]?.click()}>
                      Upload
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Notes Section */}
      <Card variant="outlined">
        <CardContent>
          <Typography
            variant="subtitle2"
            gutterBottom>
            Notes
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Add notes about the evaluation..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            disabled={!canEdit}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
